import {
  type FC,
  useState,
  useEffect,
  type ChangeEvent,
  useRef,
  useMemo,
  type ComponentPropsWithoutRef,
} from "react";
import { TextField, Button, ChoiceList } from "@cmsgov/ds-medicare-gov";
import type { County } from "../@types";
import { useTypedTranslate } from "../helpers/intlHooks";
import { isValidZipcode } from "../helpers/zipcodeHelpers";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { useCounties } from "../helpers/query-hooks/useCounties";
import classNames from "classnames";

export function countyToString(county: County, zip: string): string {
  return `${zip}, ${county.name}, ${county.state}`;
}

interface CountyPickerProps extends Pick<
  ComponentPropsWithoutRef<typeof TextField>,
  "label" | "hint"
> {
  /** must be memoized */
  onCountySelect: (county?: County, zipcode?: string) => void;
  initialZip?: string;
  initialFips?: string;
  className?: string;
  buttonLabel?: string;
}

export const CountyPicker: FC<CountyPickerProps> = ({
  hint,
  label,
  onCountySelect,
  initialZip = "",
  initialFips,
  className,
  buttonLabel,
}) => {
  // * Hooks
  const t = useTypedTranslate();
  // * Context
  const { dispatch } = useAppContext();

  // * State
  const [showInvalidZipError, setShowInvalidZipError] = useState(false);
  const [zipcode, setZipcode] = useState(initialZip);
  const [fips, setFips] = useState(initialFips);

  // * Refs
  const zipcodeInput = useRef<HTMLInputElement>();

  // * Queries
  const { counties, isFetching, isError } = useCounties(zipcode);

  // * Memos
  const selectedCounty = useMemo(
    () => counties.find(county => county.fips == fips),
    [counties, fips]
  );

  // * Functions

  const onCountyChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFips(e.target.value);
  };

  const onSubmit = (e: ChangeEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const zip = zipcodeInput.current?.value || "";

    setShowInvalidZipError(!isValidZipcode(zip));
    setZipcode(zip);
    /** This clears out the user's selected (or prefilled) zipcode choice.
     * This prevents a case where a user has selected a county, then switches zipcodes and still has a previous county selected
     * which could lead to some odd edge cases. */
    setFips("");
  };

  // * Effects

  useEffect(() => {
    // when counties changes, select the only value if length === 1
    if (counties.length === 1) {
      setFips(counties[0].fips);
    } else if (!isFetching && !selectedCounty) {
      // or else, reset to undefined if we don't have a valid county
      setFips(undefined);
    }
  }, [counties, isFetching, selectedCounty]);

  useEffect(() => {
    // when we have both values selected, we call `onCountySelect`
    // this needs to be in an effect, because it might be triggered by new data
    // from the `useCounties` endpoint
    if (selectedCounty && zipcode) {
      onCountySelect(selectedCounty, zipcode);
    }
  }, [onCountySelect, selectedCounty, zipcode]);

  return (
    <div className={classNames("CountyPicker", className)}>
      <form
        className="e2e-select-county CountyPicker__form"
        onSubmit={onSubmit}
      >
        <TextField
          hint={hint}
          label={label}
          defaultValue={zipcode}
          labelClassName="e2e-enter-zip CountyPicker__label"
          className="CountyPicker__input"
          data-cy="e2e-county-picker-zip-input"
          maxLength={5}
          name="zip"
          inputRef={(node: HTMLInputElement): void => {
            zipcodeInput.current = node;
          }}
          errorMessage={
            (isError && t("error_message.fetching_county")) ||
            (showInvalidZipError && t("county_picker.error_message"))
          }
          errorMessageClassName="ds-u-font-size--base"
        />
        <Button
          className="e2eCountyPickerSelectBtn CountyPicker__submit"
          data-cy="e2e-county-picker-select-button"
          type="submit"
          variation="solid"
          onClick={() => {
            dispatch({
              type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
              settings: {
                button: {
                  buttonStyle: AnalyticsButtonStyle.PRIMARY,
                  buttonType: AnalyticsButtonType.SUBMIT,
                  text: buttonLabel || t("county_picker.button_text"),
                },
              },
            });
          }}
        >
          {buttonLabel || t("county_picker.button_text")}
        </Button>
      </form>
      {isFetching && <p>{t("app.loading")}...</p>}
      {counties.length > 0 && (
        <ChoiceList
          name="county"
          label={t("county_picker.select_your_county")}
          type="radio"
          choices={counties.map(county => ({
            label: countyToString(county, zipcode),
            value: county.fips,
            id: county.fips,
            checked: county.fips === fips,
          }))}
          onChange={onCountyChange}
        />
      )}
    </div>
  );
};
